function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
/**
 * Just a simple utility class for displaying a popup menu.
 */
import React, { Component } from 'react';
import Log from '@deephaven/log';
import ContextActionUtils, { isPromise } from "./ContextActionUtils.js";
import GlobalContextActions from "./GlobalContextActions.js";
import "./ContextActions.css";
import { jsx as _jsx } from "react/jsx-runtime";
var log = Log.module('ContextActions');
/**
 * ContextActions that you add onto any component.
 *
 * Usage:
 * let actions = [{
 *   title: 'My Action',  // Omit the title to hide it from the context menu
 *   action: () => { alert('My Action Clicked!') }
 *   actions: []            // Submenu of actions
 *   icon: faPrint,         // Limited to FontAwesome icons for now.
 *   iconColor: '#ff0000,   // Color to use for the icon
 *   shortcut: Shortcut,    // Defaults to null
 *   isGlobal: false,       // Global context action. Defaults to false.
 *   group: ContextActions.groups.default,  // What group to group the context action with
 *   order: null,                           // Int where to order within group
 *   disabled: true         // disable action
 *   menuElement: null      // Custom menu element for displaying in context menu. When null, creates a default menu item based on title
 * }];
 *
 * <div>
 *   Right click in this container
 *   <ContextActions actions={actions}/>
 * </div>
 *
 * Right clicking the container will then build the context menu, bubbling up until an element with a ContextMenuRoot is on it.
 * You should generally have a ContextMenuRoot on the root node of your document.
 */
class ContextActions extends Component {
  static triggerMenu(element, clientX, clientY, actions) {
    if (actions.length === 0) {
      return;
    }
    var mouseEvent = new MouseEvent('contextmenu', {
      clientX,
      clientY,
      bubbles: true,
      cancelable: true
    });
    mouseEvent.contextActions = actions;
    element.dispatchEvent(mouseEvent);
  }
  constructor(props) {
    super(props);
    _defineProperty(this, "container", void 0);
    this.handleContextMenu = this.handleContextMenu.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.container = /*#__PURE__*/React.createRef();
    this.state = {
      globalActions: [],
      keyboardActions: []
    };
  }
  static getDerivedStateFromProps(props) {
    if (props.actions == null || !Array.isArray(props.actions)) {
      return {
        globalActions: [],
        keyboardActions: []
      };
    }
    var globalActions = props.actions.filter(action => !isPromise(action) && typeof action !== 'function' && action.isGlobal);
    var keyboardActions = props.actions.filter(action => !isPromise(action) && typeof action !== 'function' && (action.isGlobal === undefined || !action.isGlobal) && action.shortcut != null);
    return {
      globalActions,
      keyboardActions
    };
  }
  componentDidMount() {
    var _this$container$curre;
    if ((_this$container$curre = this.container.current) !== null && _this$container$curre !== void 0 && _this$container$curre.parentElement) {
      this.container.current.parentElement.addEventListener('contextmenu', this.handleContextMenu);
      this.container.current.parentElement.addEventListener('keydown', this.handleKeyDown);
    }
  }
  componentWillUnmount() {
    var _this$container$curre2;
    if ((_this$container$curre2 = this.container.current) !== null && _this$container$curre2 !== void 0 && _this$container$curre2.parentElement) {
      this.container.current.parentElement.removeEventListener('contextmenu', this.handleContextMenu);
      this.container.current.parentElement.removeEventListener('keydown', this.handleKeyDown);
    }
  }
  handleContextMenu(e) {
    var _this$props$ignoreCla = this.props.ignoreClassNames,
      ignoreClassNames = _this$props$ignoreCla === void 0 ? [] : _this$props$ignoreCla;
    if (ignoreClassNames.length > 0) {
      var el = e.target;
      var _loop = function _loop() {
          var _el = el,
            classList = _el.classList;
          var ignoredClassName = ignoreClassNames.find(className => classList.contains(className));
          if (ignoredClassName !== undefined) {
            log.debug2("Contextmenu event ignored based on the target className \"".concat(ignoredClassName, "\""));
            return {
              v: void 0
            };
          }
          el = el.parentElement;
        },
        _ret;
      while (el != null) {
        _ret = _loop();
        if (_ret) return _ret.v;
      }
    }
    if (!ContextActionUtils.isContextActionEvent(e)) {
      e.contextActions = [];
    }
    if (!ContextActionUtils.isContextActionEvent(e)) {
      return;
    }
    var actions = this.props.actions;
    if (actions != null) {
      var contextActions = actions;
      if (Array.isArray(contextActions)) {
        contextActions = contextActions.filter(action => isPromise(action) || typeof action === 'function' || action.isGlobal === undefined || !action.isGlobal);
      }
      e.contextActions = e.contextActions.concat(contextActions);
    }
    log.debug('Received context menu event! Menu items are now: ', e.contextActions);
  }
  handleKeyDown(e) {
    var keyboardActions = this.state.keyboardActions;
    for (var i = 0; i < keyboardActions.length; i += 1) {
      var keyboardAction = keyboardActions[i];
      if (!ContextActionUtils.actionsDisabled && keyboardAction.shortcut != null && keyboardAction.shortcut.matchesEvent(e)) {
        var _keyboardAction$actio;
        log.debug('Context hotkey matched!', e);
        (_keyboardAction$actio = keyboardAction.action) === null || _keyboardAction$actio === void 0 || _keyboardAction$actio.call(keyboardAction, e);
        e.stopPropagation();
        e.preventDefault();
        log.debug2('Matched hotkey returned false, key event not consumed');
      }
    }
  }
  render() {
    var dataTestId = this.props['data-testid'];
    var globalActions = this.state.globalActions;
    return /*#__PURE__*/_jsx("div", {
      className: "context-actions-listener",
      ref: this.container,
      "data-testid": dataTestId,
      children: /*#__PURE__*/_jsx(GlobalContextActions, {
        actions: globalActions
      })
    });
  }
}
/**
 * Group you can assign to context menu actions to group them together.
 * Lower group IDs appear at the top of the list.
 * Groups are separated by a separator item.
 * Items within groups are ordered by their order property, then by their title.
 */
_defineProperty(ContextActions, "groups", {
  default: undefined,
  high: 100,
  medium: 5000,
  low: 10000,
  global: 100000,
  edit: 100
});
export default ContextActions;
//# sourceMappingURL=ContextActions.js.map